import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useProduct } from '../../../context/productContext/ProductContextProvider';
import { Button, Dropdown, Input, Typeahead } from '../../../components';
import { useProductGroup } from '../../../context/ProductGroupContext/ProductGroupContextProvider';

const CreateProduct = ({
  closeModal,
  data,
  prodCode,
  isCreatedFromProposal,
}) => {
  const {
    productMaster,
    setProductMaster,
    error,
    createProductMaster,
    updateProduct,
    clearFormData,
  } = useProduct();
  const { getProductGroup, startLoading, stopLoading } = useProductGroup();
  const typeaheadRef = useRef(null);

  const taxCodeOptions = [
    { label: 'Yes', value: 'E' },
    { label: 'No', value: ' ' },
  ];

  const modalRef = useRef(null);
  const [response, setResponse] = useState([]);
  const itemDescriptionRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(data?.productImage || '');
  const [showPreview, setShowPreview] = useState(false);
  const [imagePath, setImagePath] = useState(data?.itemImage || '');
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    if (data) {
      //to check while updating product
      const hasBtsCost =
        data?.btsPrice != null ||
        data?.normalPrice != null ||
        data?.btsConversionFactor != null;

      setProductMaster({
        ...data,
        isBtsProduct: hasBtsCost,
        itemDescription: data.itemDescription
          ? data.itemDescription.toUpperCase()
          : '',
      });
      setLogoPreview(data.productImage || '');
      setImagePath(data.itemImage || '');
    } else {
      setProductMaster((prev) => ({
        ...prev,
        unit: 'Each',
        taxCode: 'E',
        normalPrice: null,
        btsPrice: null,
        btsConversionFactor: null,
        isBtsProduct: false,
      }));
      setLogoPreview('');
      setImagePath('');
      setImageFile(null);
    }

    return () => {
      // Cleanup function to reset productMaster
      clearFormData();
      setLogoPreview('');
      setImagePath('');
      setImageFile(null);
    };
  }, [data]);

  useEffect(() => {
    const fetchProductGroup = async () => {
      startLoading();
      try {
        const response = await getProductGroup();
        setResponse(response);
      } finally {
        const timeoutId = setTimeout(stopLoading, 500); // Adding a timeout of 500ms before stopping loading
        return () => clearTimeout(timeoutId); // Clear timeout after execution
      }
    };
    fetchProductGroup();
    return () => {
      setResponse([]); // Cleanup function to reset productGroupResponse
    };
  }, []);

  useEffect(() => {
    if (prodCode) {
      setProductMaster({ ...productMaster, itemCode: prodCode });
    }
    return () => {
      setProductMaster((prev) => ({ ...prev, itemCode: '' })); // Cleanup function to reset itemCode
    };
  }, [prodCode]);

  useEffect(() => {
    return () => {
      // Cleanup function to clear modal ref
      if (modalRef.current) {
        modalRef.current = null;
      }
    };
  }, []);

  const productGroupOptions = useMemo(
    () =>
      response.map((item) => ({
        label: `${item.productGroupCode} - ${item.productGroupName}`,
        id: item.id,
        productGroupCode: item.productGroupCode,
        productGroupName: item.productGroupName,
        catalogueNo: item.catalogueNo,
        mainProductGroup: item.mainProductGroup,
        powerBiMainProductGroup: item.powerBiMainProductGroup,
        salesDepartment: item.salesDepartment,
        data: item,
      })),
    [response],
  );

  const [selectedProductGroup, setSelectedProductGroup] = useState(
    productMaster?.productGroup?.id ? [productMaster?.productGroup] : [],
  );

  const [productGroupQuery, setProductGroupQuery] = useState(
    productMaster?.productGroup?.productGroupName || '',
  );

  useEffect(() => {
    if (data && data.id) {
      if (productMaster?.productGroup?.id) {
        const matchedOption = productGroupOptions.find(
          (option) => option.id === productMaster.productGroup.id,
        );
        if (matchedOption) {
          setSelectedProductGroup((prev) => {
            if (prev[0]?.id !== matchedOption.id) {
              return [matchedOption];
            }
            return prev;
          });
        } else {
          setSelectedProductGroup([]);
        }
      } else {
        setSelectedProductGroup([]);
      }
    }
    return () => {
      setSelectedProductGroup([]); // Cleanup function to reset selectedProductGroup
      if (typeaheadRef.current) {
        typeaheadRef.current.clear();
      }
    };
  }, [data, productMaster?.productGroup?.id, productGroupOptions]);

  const handleTypeaheadChange =
    (field, setSelected, setQuery) => (selectedOptions) => {
      const selectedOption = selectedOptions[0] || null;

      setSelected(selectedOptions);

      setProductMaster((prev) => ({
        ...prev,
        [field]: selectedOption
          ? {
              id: selectedOption.id,
              productGroupCode: selectedOption.productGroupCode,
              productGroupName: selectedOption.productGroupName,
              catalogueNo: selectedOption.catalogueNo,
              mainProductGroup: selectedOption.mainProductGroup,
              powerBiMainProductGroup: selectedOption.powerBiMainProductGroup,
              salesDepartment: selectedOption.salesDepartment,
            }
          : '', // Set as null if no option is selected
      }));

      setQuery(selectedOption ? selectedOption.productGroupName : '');
    };

  const handleTypeaheadBlur =
    (field, setSelected, setQuery, query, response) => () => {
      const matchedOption = response.find(
        (item) => item.productGroupName === query,
      );

      if (!matchedOption) {
        setSelected([]);
        setQuery('');
        setProductMaster((prev) => ({ ...prev, [field]: null }));
      }
    };

  const onChange = (e) => {
    const { name, value, type } = e.target;

    let newValue;
    if (type === 'number') {
      newValue = value === '' ? '' : parseFloat(value);
    } else if (name === 'gstFlag') {
      newValue = value === 'yes';
    } else if (name === 'itemDescription') {
      newValue = value.toUpperCase();
    } else {
      newValue = value;
    }

    setProductMaster((prev) => ({ ...prev, [name]: newValue }));
    if (name === 'itemDescription' && e.target.setSelectionRange) {
      const pos = e.target.selectionStart;
      requestAnimationFrame(() => {
        e.target.setSelectionRange(pos, pos);
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/avif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file && !allowedTypes.includes(file.type)) {
      // Handle error (you might need to add a local error state or use the context error)
      alert(
        'Invalid file type. Only .jpg, .jpeg, .png, and .avif files are allowed.',
      );
      if (fileInputRef.current) fileInputRef.current.value = null;
    } else if (file && file.size > maxSize) {
      alert('File size should not exceed 5MB');
      if (fileInputRef.current) fileInputRef.current.value = null;
    } else {
      setImageFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setImagePath(file.name);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (previewRef.current && !previewRef.current.contains(event.target)) {
        setShowPreview(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [previewRef]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (productMaster?.id > 0) {
      await updateProduct(
        productMaster,
        imageFile,
        e,
        closeModal,
        productMaster?.id,
      );
    } else {
      await createProductMaster(
        productMaster,
        imageFile,
        e,
        closeModal,
        isCreatedFromProposal,
      );
    }
    // typeaheadRef.current?.clear();
  };

  useEffect(() => {
    return () => {
      // Cleanup function to reset itemCode
      setProductMaster((prev) => ({ ...prev, itemCode: '' }));
    };
  }, [prodCode]);

  useEffect(() => {
    return () => {
      // Cleanup function to reset selectedProductGroup
      setSelectedProductGroup([]);
      if (typeaheadRef.current) {
        typeaheadRef.current.clear();
      }
    };
  }, [data, productMaster?.productGroup?.id, productGroupOptions]);

  useEffect(() => {
    return () => {
      // Cleanup function to clear modal ref
      if (modalRef.current) {
        modalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      typeaheadRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (productGroupOptions.length === 0) return;

    if (data && data.id) {
      // Edit Mode: pre-fill existing product group
      const matchedOption = productGroupOptions.find(
        (option) => option.id === data.productGroup?.id,
      );
      if (matchedOption) {
        setSelectedProductGroup([matchedOption]);
        setProductGroupQuery(matchedOption.productGroupName);
      } else {
        setSelectedProductGroup([]);
        setProductGroupQuery('');
      }
    } else {
      // Create Mode: set default only if not already set
      const defaultGroup = productGroupOptions.find(
        (opt) => opt.label === '0001 - OC PROMOTION ITEMS',
      );
      if (defaultGroup) {
        setSelectedProductGroup([defaultGroup]);
        setProductGroupQuery(defaultGroup.productGroupName);
        setProductMaster((prev) => ({
          ...prev,
          productGroup: {
            id: defaultGroup.id,
            productGroupCode: defaultGroup.productGroupCode,
            productGroupName: defaultGroup.productGroupName,
            catalogueNo: defaultGroup.catalogueNo,
            mainProductGroup: defaultGroup.mainProductGroup,
            powerBiMainProductGroup: defaultGroup.powerBiMainProductGroup,
            salesDepartment: defaultGroup.salesDepartment,
          },
        }));
      }
    }

    // Cleanup on unmount
    return () => {
      setSelectedProductGroup([]);
      setProductGroupQuery('');
      typeaheadRef.current?.clear();
    };
  }, [productGroupOptions]);

  return (
    <form ref={modalRef} onSubmit={handleSubmit}>
      <div className="lg:grid grid-cols-2 gap-3 items-center lg:w-[800px]">
        <div>
          <Input
            name="itemCode"
            type="text"
            label="Item Code"
            placeholder="Enter Product Code"
            value={productMaster?.itemCode || ''}
            onChange={onChange}
            error={error?.itemCode}
            disabled={productMaster?.id}
            required
          />
        </div>
        <div>
          <Typeahead
            ref={typeaheadRef}
            id="productGroup"
            label="Product Group"
            labelKey="label"
            options={productGroupOptions}
            placeholder="Choose product group"
            disabledCreateNew={true}
            onChange={handleTypeaheadChange(
              'productGroup',
              setSelectedProductGroup,
              setProductGroupQuery,
            )}
            onBlur={handleTypeaheadBlur(
              'productGroup',
              setSelectedProductGroup,
              setProductGroupQuery,
              productGroupQuery,
              productGroupOptions,
            )}
            selected={selectedProductGroup}
            custErr={error?.productGroup}
            isInvalid={!error?.productGroup}
            required
            query={productGroupQuery}
            setQuery={setProductGroupQuery}
          />
        </div>
      </div>
      <div className="">
        <div>
          <Input
            ref={itemDescriptionRef}
            name="itemDescription"
            type="textarea"
            label="Item Description"
            placeholder="Enter Product Description"
            value={productMaster?.itemDescription}
            onChange={onChange}
            error={error?.itemDescription}
            required
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-2 gap-3 items-center lg:w-[800px]">
        <div>
          <Input
            name="unit"
            type="text"
            label="Unit"
            placeholder="Enter Unit"
            value={productMaster?.unit}
            onChange={onChange}
            error={error?.unit}
            required
          />
        </div>
        <div>
          <div>
            <Dropdown
              name="taxCode"
              label="Tax Code"
              value={productMaster?.taxCode}
              options={taxCodeOptions}
              onChange={onChange}
            />
          </div>
        </div>
        <div>
          <Input
            name="avgCost"
            type="number"
            label="Average Cost"
            placeholder="Enter Average Cost"
            value={productMaster?.avgCost}
            onChange={onChange}
            error={error?.avgCost}
            required
          />
        </div>
        <div className="lg:grid grid-cols-2 gap-3 items-center lg:w-[800px]">
          <div className="col-span-2">
            <label className="block font-semibold mb-1">Is BTS Product</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="isBtsProduct"
                  value="yes"
                  checked={productMaster?.isBtsProduct === true}
                  onChange={() =>
                    setProductMaster((prev) => ({
                      ...prev,
                      isBtsProduct: true,
                    }))
                  }
                />
                Yes
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="isBtsProduct"
                  value="no"
                  checked={productMaster?.isBtsProduct === false}
                  onChange={() =>
                    setProductMaster((prev) => ({
                      ...prev,
                      isBtsProduct: false,
                      btsPrice: '',
                      normalPrice: '',
                      btsConversionFactor: '',
                    }))
                  }
                />
                No
              </label>
            </div>
          </div>
        </div>
        {productMaster?.isBtsProduct && (
          <div className="lg:grid grid-cols-2 gap-3 items-center lg:w-[800px]">
            <div>
              <Input
                name="btsPrice"
                type="number"
                label="BTS Cost"
                placeholder="Enter BTS Cost"
                value={productMaster?.btsPrice ?? ''}
                onChange={onChange}
                error={error?.btsPrice}
                required={productMaster?.isBtsProduct}
              />
            </div>
            <div>
              <Input
                name="normalPrice"
                type="number"
                label="Normal Cost"
                placeholder="Enter Normal Cost"
                value={productMaster?.normalPrice ?? ''}
                onChange={onChange}
                error={error?.normalPrice}
                required={productMaster?.isBtsProduct}
              />
            </div>
            <div>
              <Input
                name="btsConversionFactor"
                type="number"
                label="BTS Conversion Factor"
                placeholder="Enter BTS Conversion Factor"
                value={productMaster?.btsConversionFactor ?? ''}
                onChange={onChange}
                error={error?.btsConversionFactor}
                required={productMaster?.isBtsProduct}
              />
            </div>
          </div>
        )}
      </div>
      {/* {!productMaster?.id && (
        <div className="lg:grid grid-cols-2 gap-3 items-center lg:w-[800px] mt-4">
          <div>
            <Input
              ref={fileInputRef}
              id="fileInput"
              label="Upload Image"
              type="file"
              accept=".jpg,.png,.jpeg,.avif"
              onChange={handleFileChange}
              error={error?.file}
              multiple={false}
            />
          </div>
          <div className="pt-2 relative">
            {imagePath && (
              <div className="mb-2 text-gray-500 text-sm">{imagePath}</div>
            )}
            {logoPreview && (
              <>
                <span
                  className="text-blue-500 cursor-pointer"
                  onClick={() => setShowPreview(true)}>
                  Preview Image
                </span>
                {showPreview && (
                  <div className="fixed inset-0 z-10 flex items-center justify-center bg-opacity-50 lg:pl-72 backdrop-blur-sm">
                    <div
                      ref={previewRef}
                      className="p-4 rounded-lg backdrop-blur-lg">
                      <img
                        src={logoPreview}
                        alt="Product Logo"
                        className=" h-96 object-cover"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )} */}
      <div className="flex justify-start gap-2 mt-10 m-2">
        <Button type="button" className="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="px-4 py-2 bg-yellow-500 text-white rounded-3xl">
          {productMaster?.id ? 'Update' : 'Save'}
        </Button>
      </div>
    </form>
  );
};

export default CreateProduct;
